// The depth pass: the depth tints of chart card 09 in the look of mockup
// round 116 with the depth light of round 143, ONE full-world quad (R2713,
// R3229, ui-map-interface.md §17.3.09).
//
// The depth plane (`uDepth`, R8UI, one texel a cell) holds the band of
// each cell, 0 for no band. Three pictures share the pass:
//
//  * **The tint.** Three inks a band (R3229, round 143 card 08): the dim
//    ink ADDED to the frame (`uDpLight`, rgb with no alpha, the lit
//    shallows), the deep ink over it (`uDpAlpha`, the dark), and the hue
//    of the band over the dark (`uDpHue`, the cold blue deep and the indigo
//    abyss). The FEATHERED look (card 02) blends the three of the cell and
//    its six neighbours with the kernel of the prelude, so a
//    band edge is a slope about one hex wide. A hardware linear filter
//    would not do: the texel grid is the odd-r hex grid, and a bilinear tap
//    smears a sheared picture. The STEPPED look (card 01) takes the alpha
//    figures of the cell's own band, a hard hex step. The CHART look
//    (round 145 card 02, R3278, `uDpSoft` 2) blends the band of the ring
//    and cuts it at 1.5, 2.5 and 3.5: the distance to a level is the field
//    step over the gradient length, so each band takes its own flat figures
//    up to a smooth edge one device pixel wide. A cell with no band takes
//    nothing, and a cell with no band adds nothing to the blend, so rock
//    keeps the edge it has today.
//  * **The shoal stipple** (card 71, round 116 card 05). Each cell
//    carries `uDpStipPer` dots of its band at points a hash of the cell
//    picks. The hash and the pick are the twins of `layers/depth.ts`
//    `stippleDots`, which the tests read.
//  * **The edge seam** (card 72, round 116 card 09). The blended band field
//    has an analytic gradient, so the distance to the level halfway between
//    two bands is the field step over the gradient length. The seam stands
//    on that level, and an own hull lights it: the alpha is (1 - d / R)² of
//    the nearest hull of `uDpHulls`, and zero past `uDpSeamR`.
//  * **The lanterns** (R3229, round 143 card 07). The dim ink ADDED round
//    each lantern of `uDpLamps` (an own hull or an own hold: x, y and the
//    radius), falling in straight lines from `uDpLampA` at the centre to
//    `uDpLampMidA` at `uDpLampAt` of the radius and to 0 at the radius.
//    Pools add up to `uDpLampA`; over it the sum bends toward `uDpLampMax`
//    (R3238). They light every cell of the grid, rock too. The twins are
//    `lanternAlpha` and `lanternCap` of `layers/depth.ts`.
//  * **The depth heat look** (R2870), while `uDpHeat` holds: each band
//    takes its own ink and alpha of `uDpHeatInk`, a hard hex step, and a
//    contour of `uDpEdgeW` screen px stands on the shallower side of each
//    edge to a deeper band. The edge is the perpendicular bisector of the
//    two cell centres, so the distance to it is exact. No stipple, no
//    seam and no lantern draw in it.
//  * **On paper** (R3295). The light identity paints one composite of
//    `DEPTH_BAND_LIGHT` a band through the hue slot (`depthLookCfg`), with
//    no dark and no added light, and each lantern lays the dim ink over
//    as a darker pool (`LANTERN_POOL`). The source of the dark identity
//    does not change by one character.
//
// No figure is a literal (prelude rule 1): the three loop bounds are the
// pack's `max` of card 71, `hulls` of card 72 and the lantern slots of
// card 09, written into the source.
// The hash constants are the hash, not a figure of the look.

import { CHART } from '../../gen/presentation';
import { LIGHT } from '../../identity/pick';
import { HEX_SIZE } from '../../gen/balance';
import { PRELUDE, SHARED_UNIFORMS, type UniformSpec } from './prelude';
import { worldProgram } from '../gfx/worldquad';
import type { ProgramSource } from './program';
import { depthBodyWgsl } from './wgsl/depth';
import { LANTERN_SLOTS, SEAM_HULL_SLOTS, STIPPLE_MAX } from '../../depthlook';

const T = CHART.pieces.depth_tints;

/** The lanterns of the dark identity: the dim ink ADDED (R3229). */
export const LANTERN_ADD = `  // The lanterns add the dim ink inside the grid, rock too.
  if (uDpLampN > 0 && inGrid(c0)) o.rgb += uDpLightInk * lanternLight(p);
`;

/** The lanterns on paper (R3295): added light disappears there, so each
 *  pool lays the dim ink over, a darker pool. The layer scales the alphas
 *  by `AP_GLOW_K`. */
export const LANTERN_POOL = `  // The lanterns lay the dim ink over inside the grid, rock too (R3295).
  if (uDpLampN > 0 && inGrid(c0)) o = over(o, uDpLightInk, lanternLight(p));
`;
const STIPPLE = CHART.pieces.shoal_stipple;
const SEAM = CHART.pieces.edge_seam;

/** The depth pass's own uniforms, on top of `SHARED_UNIFORMS`. */
export const DEPTH_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uDpSoft', type: 'int', value: 1, dynamic: true, note: '1 the feathered look, 0 the stepped look, 2 the chart look (R3278)' },
  { name: 'uDpAlpha', type: 'vec4', value: [...T.modern.arr.band_alpha], dynamic: true, note: 'the dark alpha of bands 1 to 4' },
  { name: 'uDpInk', type: 'vec3', value: [0, 0, 0], dynamic: true, note: 'the ink of the dark' },
  { name: 'uDpLight', type: 'vec4', value: [...T.modern.arr.light_alpha], dynamic: true, note: 'the light alpha of bands 1 to 4 (R3229)' },
  { name: 'uDpLightInk', type: 'vec3', value: [0, 0, 0], dynamic: true, note: 'the ink of the light and the lanterns' },
  {
    name: 'uDpHue', type: 'vec4[]', value: new Array<number>(16).fill(0), dynamic: true,
    note: 'the hue ink (rgb) and alpha (a) of bands 1 to 4 (R3229)',
  },
  { name: 'uDpMark', type: 'vec3', value: [1, 1, 1], dynamic: true, note: 'the ink of the stipple and the seam' },
  { name: 'uDpRadius', type: 'float', value: T.fig.feather_k * HEX_SIZE, note: 'the feather kernel, world px' },
  { name: 'uDpStipple', type: 'int', value: 0, dynamic: true },
  { name: 'uDpStipPer', type: 'vec4', value: [...STIPPLE.arr.per_cell], note: 'dots a cell, bands 1 to 4' },
  { name: 'uDpStipR', type: 'float', value: STIPPLE.modern.fig.dot_r, dynamic: true, note: 'world px' },
  { name: 'uDpStipA', type: 'float', value: STIPPLE.modern.fig.dot_alpha, dynamic: true },
  { name: 'uDpStipSpread', type: 'float', value: STIPPLE.fig.spread_px, note: 'world px from the cell centre' },
  { name: 'uDpSeam', type: 'int', value: 1, dynamic: true },
  { name: 'uDpSeamR', type: 'float', value: SEAM.fig.radius_px, note: 'world px an own hull lights' },
  { name: 'uDpGlowW', type: 'float', value: SEAM.fig.glow_px },
  { name: 'uDpGlowA', type: 'float', value: SEAM.modern.fig.glow_alpha, dynamic: true },
  { name: 'uDpLineW', type: 'float', value: SEAM.fig.line_px },
  { name: 'uDpLineA', type: 'float', value: SEAM.modern.fig.line_alpha, dynamic: true },
  { name: 'uDpHeat', type: 'int', value: 0, dynamic: true, note: '1 the depth heat look (R2870)' },
  {
    name: 'uDpHeatInk', type: 'vec4[]', value: new Array<number>(16).fill(0), dynamic: true,
    note: 'the heat ink (rgb) and alpha (a) of bands 1 to 4',
  },
  { name: 'uDpEdgeA', type: 'float', value: T.fig.heat_edge_alpha, dynamic: true, note: 'the heat contour alpha' },
  { name: 'uDpEdgeW', type: 'float', value: T.fig.heat_edge_px, dynamic: true, note: 'the heat contour, screen px' },
  { name: 'uDpLampN', type: 'int', value: 0, dynamic: true, note: 'lanterns in uDpLamps (R3229)' },
  {
    name: 'uDpLamps', type: 'vec4[]', value: new Array<number>(LANTERN_SLOTS * 4).fill(0), dynamic: true,
    note: 'a lantern a vec4: x, y and the radius, world px',
  },
  { name: 'uDpLampA', type: 'float', value: T.modern.fig.lantern_alpha, dynamic: true, note: 'the lantern alpha at the centre' },
  { name: 'uDpLampMidA', type: 'float', value: T.modern.fig.lantern_mid_alpha, dynamic: true, note: 'the lantern alpha at uDpLampAt' },
  { name: 'uDpLampAt', type: 'float', value: T.fig.lantern_mid_at, note: 'the share of the radius of the mid alpha' },
  { name: 'uDpLampMax', type: 'float', value: T.modern.fig.lantern_max_alpha, dynamic: true, note: 'the cap of the summed lantern alpha (R3238)' },
  { name: 'uDpHullN', type: 'int', value: 0, dynamic: true, note: 'own hulls in uDpHulls' },
  {
    name: 'uDpHulls', type: 'vec4[]', value: new Array<number>(SEAM_HULL_SLOTS * 2).fill(0), dynamic: true,
    note: 'two own hulls a vec4, world px',
  },
];

/** Every uniform the depth program declares. */
export const DEPTH_ALL_UNIFORMS: readonly UniformSpec[] = [...SHARED_UNIFORMS, ...DEPTH_UNIFORMS];

const BODY = `
uniform int   uDpSoft;
uniform vec4  uDpAlpha;
uniform vec3  uDpInk;
uniform vec4  uDpLight;
uniform vec3  uDpLightInk;
uniform vec4  uDpHue[4];
uniform vec3  uDpMark;
uniform float uDpRadius;
uniform int   uDpStipple;
uniform vec4  uDpStipPer;
uniform float uDpStipR;
uniform float uDpStipA;
uniform float uDpStipSpread;
uniform int   uDpSeam;
uniform float uDpSeamR;
uniform float uDpGlowW;
uniform float uDpGlowA;
uniform float uDpLineW;
uniform float uDpLineA;
uniform int   uDpHeat;
uniform vec4  uDpHeatInk[4];
uniform float uDpEdgeA;
uniform float uDpEdgeW;
uniform int   uDpLampN;
uniform vec4  uDpLamps[${LANTERN_SLOTS}];
uniform float uDpLampA;
uniform float uDpLampMidA;
uniform float uDpLampAt;
uniform float uDpLampMax;
uniform int   uDpHullN;
uniform vec4  uDpHulls[${SEAM_HULL_SLOTS / 2}];

// A cell outside the grid has no band.
int bandOf(ivec2 c) {
  return inGrid(c) ? int(texelFetch(uDepth, c, 0).r) : 0;
}

// The dark alpha of band b, 1 to 4.
float bandAlpha(int b) { return uDpAlpha[b - 1]; }

// layers/depth.ts hash32: the integer hash of the stipple pick.
uint dpHash(uint i) {
  uint h = (i ^ 0x9e3779b9u) * 2654435761u;
  h ^= h >> 15u;
  h *= 2246822519u;
  return h ^ (h >> 13u);
}

// The coverage of the stipple dots of cell c0 at p (layers/depth.ts
// stippleDots): the whole dots of the band, and one more on a hash under
// the fraction.
float stippleCov(vec2 p, ivec2 c0, int b0) {
  float per = uDpStipPer[b0 - 1];
  uint cell = uint(c0.y * uGrid.x + c0.x);
  int n = int(floor(per));
  if (float(dpHash(cell * 31u + 97u) % 1000u) < fract(per) * 1000.0) n += 1;
  vec2 ctr = cellCenter(c0);
  float px = worldPx();
  float cov = 0.0;
  for (int k = 0; k < ${STIPPLE_MAX}; k++) {
    if (k >= n) break;
    uint h = dpHash(cell * 31u + uint(k));
    uint h2 = dpHash(h + 11u);
    float ang = float(h % 6283u) / 1000.0;
    float d = sqrt(float(h2 % 1000u) / 1000.0) * uDpStipSpread;
    vec2 q = ctr + vec2(cos(ang), sin(ang)) * d;
    cov = max(cov, fillCov(uDpStipR - length(p - q), px));
  }
  return cov;
}

// layers/depth.ts seamFade: (1 - d / R)² of the nearest own hull.
float hullNear(vec2 p) {
  float best = 0.0;
  for (int i = 0; i < ${SEAM_HULL_SLOTS}; i++) {
    if (i >= uDpHullN) break;
    vec4 h = uDpHulls[i / 2];
    vec2 q = (i % 2 == 0) ? h.xy : h.zw;
    float t = max(0.0, 1.0 - length(p - q) / uDpSeamR);
    best = max(best, t * t);
  }
  return best;
}

// The depth heat look (R2870): the band's own ink, a hard step, and a
// contour on the shallower side of each edge to a deeper band.
vec4 heatTint(vec2 p, ivec2 c0, int b0) {
  vec4 k = uDpHeatInk[b0 - 1];
  vec4 o = vec4(k.rgb * k.a, k.a);
  vec2 ctr = cellCenter(c0);
  float w = min(uDpEdgeW / max(uZoom, 1e-6), 0.4 * uHexSize);
  float px = worldPx();
  float cov = 0.0;
  for (int i = 0; i < 6; i++) {
    ivec2 c = neighbor(c0, i);
    int b = bandOf(c);
    if (b <= b0) continue;
    vec2 n = cellCenter(c) - ctr;
    float hd = 0.5 * length(n);
    float d = hd - dot(p - ctr, n / (2.0 * hd));
    cov = max(cov, lineCov(d - 0.5 * w, 0.5 * w, px));
  }
  return over(o, uDpMark, uDpEdgeA * cov);
}

// A hue of uDpHue, premultiplied.
vec4 premul(vec4 h) { return vec4(h.rgb * h.a, h.a); }

// The chart look (R3278): the share of each band at p, from the blended
// band f and its gradient g. Each level k + 0.5 is a crisp edge one device
// pixel wide; depth-edges.ts chartWeights is the twin.
vec4 chartWeights(float f, vec2 g) {
  float gn = length(g);
  float px = worldPx();
  vec3 lv = vec3(1.5, 2.5, 3.5);
  vec3 s = gn > 1e-6 ? clamp((vec3(f) - lv) / (gn * px) + 0.5, 0.0, 1.0) : step(lv, vec3(f));
  return vec4(1.0 - s.x, s.x - s.y, s.y - s.z, s.z);
}

// layers/depth.ts lanternCap: the soft cap of the summed light (R3238).
float lanternCap(float s) {
  float room = uDpLampMax - uDpLampA;
  if (s <= uDpLampA) return s;
  if (room <= 0.0) return min(s, uDpLampMax);
  float x = (s - uDpLampA) / room;
  return uDpLampA + room * x / (1.0 + x);
}

// layers/depth.ts lanternAlpha: the summed alpha of the lanterns at p,
// under the soft cap.
float lanternLight(vec2 p) {
  float sum = 0.0;
  for (int i = 0; i < ${LANTERN_SLOTS}; i++) {
    if (i >= uDpLampN) break;
    vec4 l = uDpLamps[i];
    float t = length(p - l.xy) / max(l.z, 1e-6);
    if (t >= 1.0) continue;
    sum += t < uDpLampAt
      ? mix(uDpLampA, uDpLampMidA, t / uDpLampAt)
      : uDpLampMidA * (1.0 - t) / (1.0 - uDpLampAt);
  }
  return lanternCap(sum);
}

// The tint of a cell with a band: the light, the dark and the hue, then
// the marks.
vec4 tint(vec2 p, ivec2 c0, int b0) {
  // The blend over the cell and its ring: the three inks, and the band
  // with its gradient for the seam. A cell with no band adds nothing.
  float invR2 = 1.0 / (uDpRadius * uDpRadius);
  float den = 0.0, na = 0.0, nl = 0.0, nb = 0.0;
  vec4 nh = vec4(0.0);
  vec2 dDen = vec2(0.0), dNb = vec2(0.0);
  if (uDpSoft != 0 || uDpSeam == 1) {
    for (int i = 0; i < 7; i++) {
      ivec2 c = (i == 0) ? c0 : neighbor(c0, i - 1);
      int b = bandOf(c);
      if (b == 0) continue;
      float k; vec2 dk;
      kern(p, cellCenter(c), invR2, k, dk);
      den += k;
      dDen += dk;
      na += k * bandAlpha(b);
      nl += k * uDpLight[b - 1];
      vec4 h = uDpHue[b - 1];
      nh += k * vec4(h.rgb * h.a, h.a);
      nb += k * float(b);
      dNb += dk * float(b);
    }
  }
  float a = bandAlpha(b0);
  float l = uDpLight[b0 - 1];
  vec4 h0 = uDpHue[b0 - 1];
  vec4 hp = vec4(h0.rgb * h0.a, h0.a);
  if (uDpSoft == 1 && den > 1e-6) { a = na / den; l = nl / den; hp = nh / den; }
  if (uDpSoft == 2 && den > 1e-6) {
    vec4 w = chartWeights(nb / den, (dNb - (nb / den) * dDen) / den);
    a = dot(w, uDpAlpha);
    l = dot(w, uDpLight);
    hp = w.x * premul(uDpHue[0]) + w.y * premul(uDpHue[1]) + w.z * premul(uDpHue[2]) + w.w * premul(uDpHue[3]);
  }
  // The light adds rgb and no alpha; the dark and the hue lie over it.
  vec4 o = over(vec4(uDpLightInk * l, 0.0), uDpInk, a);
  o = hp + o * (1.0 - hp.a);

  if (uDpStipple == 1) o = over(o, uDpMark, uDpStipA * stippleCov(p, c0, b0));

  if (uDpSeam == 1 && uDpHullN > 0 && den > 1e-6) {
    float lit = hullNear(p);
    if (lit > 0.0) {
      float f = nb / den;
      vec2 g = (dNb - f * dDen) / den;
      float gn = length(g);
      if (gn > 1e-6) {
        float lv = clamp(floor(f) + 0.5, 1.5, 3.5);
        float d = abs(f - lv) / gn;
        float px = worldPx();
        o = over(o, uDpMark, uDpGlowA * lit * lineCov(d, 0.5 * uDpGlowW, px));
        o = over(o, uDpMark, uDpLineA * lit * lineCov(d, 0.5 * uDpLineW, px));
      }
    }
  }
  return o;
}

void main() {
  vec2 p = vWorld;
  ivec2 c0 = cellAt(p);
  int b0 = bandOf(c0);
  if (uDpHeat == 1) { fragColor = b0 == 0 ? vec4(0.0) : heatTint(p, c0, b0); return; }
  vec4 o = vec4(0.0);
  if (b0 > 0) o = tint(p, c0, b0);
${LIGHT ? LANTERN_POOL : LANTERN_ADD}  fragColor = o;
}
`;

/** The complete depth fragment shader, prelude included. */
export const DEPTH_FRAG = PRELUDE + BODY;

/** The depth pass, as both backends read it. */
export function depthProgram(): ProgramSource {
  return worldProgram({
    name: 'depth', fragment: DEPTH_FRAG, raw: true, uniforms: DEPTH_ALL_UNIFORMS,
    wgslBody: depthBodyWgsl(),
  });
}
